// NHS Purchase Options — custom element
class NhsPurchaseOptions extends HTMLElement {
  constructor() {
    super();
    this.purchaseType = 'onetime';
    this.selectedVariantId = null;
    this.selectedFrequencyDays = 30;
    this.data = null;
    this.sellingPlansLoaded = false;
  }

  connectedCallback() {
    // Parse JSON data
    const dataEl = this.querySelector('.nhs-po__data');
    if (dataEl) {
      try { this.data = JSON.parse(dataEl.textContent); } catch (e) { console.error('NHS PO: bad JSON', e); }
    }

    // Bind purchase type toggle
    this.querySelectorAll('.nhs-po__type-radio').forEach(radio => {
      radio.addEventListener('change', () => this.onTypeChange(radio.value));
    });

    this.querySelectorAll('.nhs-po__type-card').forEach(card => {
      card.addEventListener('click', (e) => {
        if (e.target.closest('.nhs-po__type-radio')) return;
        const radio = card.querySelector('.nhs-po__type-radio');
        if (radio && !radio.checked) {
          radio.checked = true;
          this.onTypeChange(radio.value);
        }
      });
    });

    // Bind quantity card selection
    this.querySelectorAll('.nhs-po__variant-radio').forEach(radio => {
      radio.addEventListener('change', () => this.onVariantSelect(radio.value));
    });

    this.querySelectorAll('.nhs-po__quantity-card').forEach(card => {
      card.addEventListener('click', (e) => {
        if (e.target.closest('.nhs-po__variant-radio')) return;
        const radio = card.querySelector('.nhs-po__variant-radio');
        if (radio) {
          radio.checked = true;
          this.onVariantSelect(radio.value);
        }
      });
    });

    // Bind frequency pill clicks
    this.querySelectorAll('.nhs-po__frequency-pill').forEach(pill => {
      pill.addEventListener('click', (e) => {
        e.preventDefault();
        const container = pill.closest('.nhs-po__frequency-options');
        container.querySelectorAll('.nhs-po__frequency-pill').forEach(p => p.classList.remove('active'));
        pill.classList.add('active');
        this.selectedFrequencyDays = parseInt(pill.dataset.days);
        this.updateFrequencyText();
        this.syncSellingPlan();
      });
    });

    // Initialize
    const checkedVariant = this.querySelector('.nhs-po__variant-radio:checked');
    if (checkedVariant) {
      this.selectedVariantId = parseInt(checkedVariant.value);
    }

    // Set initial frequency based on selected variant
    if (this.selectedVariantId) {
      this.updateFrequency(String(this.selectedVariantId));
    }

    this.updateTypeCards();
    this.updatePrices();
    this.updateButtonText();

    // Fetch selling plans from product API, then sync
    if (this.data && this.data.productHandle) {
      fetch('/products/' + this.data.productHandle + '.js')
        .then(r => r.json())
        .then(product => {
          if (product.selling_plan_groups && product.selling_plan_groups.length > 0) {
            const plans = product.selling_plan_groups[0].selling_plans || [];
            this.data.sellingPlans = plans.map(sp => ({ id: sp.id, name: sp.name }));
            this.sellingPlansLoaded = true;
            // Now sync with the correct plan
            this.syncSellingPlan();
          }
        })
        .catch(() => {});
    }
  }

  onTypeChange(type) {
    this.purchaseType = type;
    this.updateTypeCards();
    this.syncSellingPlan();
    this.updatePrices();
    this.updateButtonText();
  }

  updateTypeCards() {
    this.querySelectorAll('.nhs-po__type-radio').forEach(r => {
      r.checked = r.value === this.purchaseType;
    });
    this.querySelectorAll('.nhs-po__type-card').forEach(card => {
      card.classList.toggle('active', card.dataset.type === this.purchaseType);
    });
  }

  onVariantSelect(variantId) {
    this.selectedVariantId = parseInt(variantId);

    this.querySelectorAll('.nhs-po__quantity-card').forEach(card => {
      card.classList.toggle('selected', card.dataset.variantId === variantId);
    });

    this.syncVariant();
    this.updatePrices();
    this.updateFrequency(variantId);
    this.syncSellingPlan();
    this.updateButtonText();
    this.triggerVariantChange();
  }

  updatePrices() {
    const isSub = this.purchaseType === 'subscribe';

    this.querySelectorAll('.nhs-po__quantity-card').forEach(card => {
      const perUnitEl = card.querySelector('.nhs-po__card-per-unit');
      if (perUnitEl) {
        const onetimePer = parseInt(perUnitEl.dataset.onetimePer);
        const subPer = parseInt(perUnitEl.dataset.subPer);
        perUnitEl.textContent = this.formatMoney(isSub ? subPer : onetimePer) + '/bottle';
      }
      const totalEl = card.querySelector('.nhs-po__card-total');
      if (totalEl) {
        const onetimeTotal = parseInt(totalEl.dataset.onetimeTotal);
        const subTotal = parseInt(totalEl.dataset.subTotal);
        totalEl.textContent = this.formatMoney(isSub ? subTotal : onetimeTotal);
      }
    });

    const selectedCard = this.querySelector('.nhs-po__quantity-card.selected');
    if (!selectedCard) return;

    const onetimePrice = parseInt(selectedCard.dataset.onetimePrice);
    const subPrice = parseInt(selectedCard.dataset.subPrice);
    const comparePrice = selectedCard.dataset.comparePrice !== 'null' ? parseInt(selectedCard.dataset.comparePrice) : null;

    const subPriceEl = this.querySelector('[data-price-type="subscribe"]');
    if (subPriceEl) {
      const originalForSub = comparePrice || onetimePrice;
      subPriceEl.innerHTML = '<span class="compare-price">' + this.formatMoney(originalForSub) + '</span> ' + this.formatMoney(subPrice);
    }

    const otPriceEl = this.querySelector('[data-price-type="onetime"]');
    if (otPriceEl) {
      if (comparePrice && comparePrice > onetimePrice) {
        otPriceEl.innerHTML = '<span class="compare-price">' + this.formatMoney(comparePrice) + '</span> ' + this.formatMoney(onetimePrice);
      } else {
        otPriceEl.textContent = this.formatMoney(onetimePrice);
      }
    }
  }

  updateFrequency(variantId) {
    const card = this.querySelector('.nhs-po__quantity-card[data-variant-id="' + variantId + '"]');
    if (!card) return;
    const bottleCount = parseInt(card.dataset.bottleCount) || 1;
    const container = document.getElementById('nhs-frequency-options');
    if (!container) return;

    const pills = container.querySelectorAll('.nhs-po__frequency-pill');

    if (bottleCount <= 2) {
      pills.forEach(p => {
        const d = parseInt(p.dataset.days);
        if (d <= 90) {
          p.style.display = '';
        } else {
          p.style.display = 'none';
          p.classList.remove('active');
        }
      });
      // Default to 30 if no visible pill is active
      const activePill = container.querySelector('.nhs-po__frequency-pill.active:not([style*="display: none"])');
      if (!activePill) {
        pills.forEach(p => p.classList.remove('active'));
        const first = container.querySelector('[data-days="30"]');
        if (first) first.classList.add('active');
        this.selectedFrequencyDays = 30;
      }
    } else if (bottleCount === 3) {
      pills.forEach(p => {
        if (parseInt(p.dataset.days) === 90) {
          p.style.display = '';
          p.classList.add('active');
        } else {
          p.style.display = 'none';
          p.classList.remove('active');
        }
      });
      this.selectedFrequencyDays = 90;
    } else if (bottleCount >= 6) {
      pills.forEach(p => {
        p.style.display = 'none';
        p.classList.remove('active');
      });
      let pill180 = container.querySelector('[data-days="180"]');
      if (!pill180) {
        pill180 = document.createElement('button');
        pill180.type = 'button';
        pill180.className = 'nhs-po__frequency-pill';
        pill180.dataset.days = '180';
        pill180.textContent = '180-Days';
        pill180.addEventListener('click', (e) => {
          e.preventDefault();
          container.querySelectorAll('.nhs-po__frequency-pill').forEach(p => p.classList.remove('active'));
          pill180.classList.add('active');
          this.selectedFrequencyDays = 180;
          this.updateFrequencyText();
          this.syncSellingPlan();
        });
        container.appendChild(pill180);
      }
      pill180.style.display = '';
      pill180.classList.add('active');
      this.selectedFrequencyDays = 180;
    }

    this.updateFrequencyText();
  }

  updateFrequencyText() {
    const freqText = document.getElementById('nhs-frequency-text');
    if (freqText) freqText.textContent = this.selectedFrequencyDays;
  }

  syncVariant() {
    const form = document.querySelector('product-form form, form[action="/cart/add"]');
    if (!form) return;
    let input = form.querySelector('input[name="id"]');
    if (input) {
      input.value = this.selectedVariantId;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  syncSellingPlan() {
    const form = document.querySelector('product-form form, form[action="/cart/add"]');
    if (!form) return;

    let input = form.querySelector('input[name="selling_plan"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'selling_plan';
      form.appendChild(input);
    }

    if (this.purchaseType === 'subscribe' && this.data) {
      const days = this.selectedFrequencyDays || 30;
      const months = Math.round(days / 30);
      const plans = this.data.sellingPlans || [];
      let matchedPlan = null;

      for (const plan of plans) {
        if (!plan.name) continue;
        const name = plan.name.toLowerCase();
        if (months === 1 && name.includes('every month') && !name.includes('every 2') && !name.includes('every 3') && !name.includes('every 6')) {
          matchedPlan = plan;
          break;
        } else if (months > 1 && name.includes('every ' + months + ' month')) {
          matchedPlan = plan;
          break;
        }
      }

      // If plans haven't loaded yet, use the default ID
      if (!matchedPlan && plans.length > 0) {
        matchedPlan = plans[0];
      }

      if (matchedPlan) {
        input.value = matchedPlan.id;
      } else {
        // Plans not loaded yet — use default selling plan ID
        input.value = this.data.sellingPlanId || '';
      }
    } else {
      input.value = '';
    }
  }

  updateButtonText() {
    const btn = document.querySelector('.product-form__submit');
    if (!btn) return;
    const span = btn.querySelector('span') || btn;

    const selectedCard = this.querySelector('.nhs-po__quantity-card.selected');
    if (!selectedCard) {
      span.textContent = 'ADD TO CART';
      return;
    }

    const isSub = this.purchaseType === 'subscribe';
    const price = isSub ? parseInt(selectedCard.dataset.subPrice) : parseInt(selectedCard.dataset.onetimePrice);
    span.textContent = 'ADD TO CART - ' + this.formatMoney(price);
  }

  triggerVariantChange() {
    const url = new URL(window.location);
    url.searchParams.set('variant', this.selectedVariantId);
    window.history.replaceState({}, '', url);

    const variantSelects = document.querySelector('variant-selects');
    if (variantSelects) {
      const variantData = this.data?.variants?.find(v => v.id === this.selectedVariantId);
      if (variantData) {
        const radios = variantSelects.querySelectorAll('input[type="radio"]');
        radios.forEach(r => {
          if (r.value === variantData.title) {
            r.checked = true;
            r.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }
    }
  }

  formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }
}

customElements.define('nhs-purchase-options', NhsPurchaseOptions);
